import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Card,
    CardHeader,
    CardContent,
    Avatar,
    TextField,
    Typography,
} from '@material-ui/core';
import AutoComplete from '@material-ui/lab/Autocomplete';
import { TransferList } from '@commonClient2/components';
// eslint-disable-next-line no-unused-vars
import { UPDATE_SETUP } from '../../../store/setup';

const InputTitle = (props) => (
    <Typography
        component="p"
        className="input-label"
        {...props}
    >
        {props.children}
    </Typography>
);

const InputDisclaimer = (props) => (
    <p {...props}>
        <small className="input-disclaimer">
            {props.children}
        </small>
    </p>
);

/**
 * @param {{
 *  stepIndex: number
 *  validationErrors: {[Key: string] : boolean}
 *  errorRef: import('react').MutableRefObject<HTMLInputElement>
 *  disabledSections: {[Key: string] : boolean}
 * }} props
 */
function DataviewSection({
    stepIndex, validationErrors, errorRef, disabledSections,
}) {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin.setupEdit.displayDataStep' });

    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminSetup, adminAppData } = useSelector((state) => state);
    const {
        dataviewId,
        dataviewDisplayedCols,
    } = adminSetup;
    const { dataviews } = adminAppData;

    const dataviewFields = dataviews.find((x) => x.id === dataviewId)?.fields.map((x) => ({
        id: x.key,
        label: x.label,
        selected: dataviewDisplayedCols.includes(x.key),
    })) || [];

    /**
     * @param {string} target
     * @param {string | string[] | boolean} value
     */
    const updateSetupStore = (target, value) => {
        if (target === 'weeklyHours' && !value) { // Reset break rule conditions
            dispatch({
                type: UPDATE_SETUP,
                payload: {
                    [target]: value,
                    weeklyHoursByDefault: false,
                    breakRules: [],
                },
            });
            return;
        }
        dispatch({
            type: UPDATE_SETUP,
            payload: {
                [target]: value,
            },
        });
    };

    return (
        <Card className="card">
            <CardHeader
                ref={errorRef}
                className="card--header"
                color="textSecondary"
                gutterBottom
                avatar={<Avatar className="avatar">{stepIndex}</Avatar>}
                title={t('title')}
            />
            <CardContent>
                <div>
                    <InputTitle>{t('dataviewInput.label')}</InputTitle>
                    <AutoComplete
                        id="dataview-select"
                        value={dataviews.find((x) => x.id === dataviewId)}
                        options={dataviews}
                        onChange={(_event, optionSelected) => updateSetupStore('dataviewId', optionSelected.id)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={t('dataviewInput.placeholder')}
                            />
                        )}
                        getOptionLabel={(option) => option.name}
                        includeInputInList
                        disableClearable
                        disabled={disabledSections.dataviewId}
                    />
                    <InputDisclaimer>{t('dataviewInput.disclaimer')}</InputDisclaimer>
                </div>
                <div>
                    {
                        validationErrors.dataviewId ? (
                            <span style={{ color: 'red' }}>{t('dataviewInput.error')}</span>) : null
                    }
                    {
                        dataviews.find((x) => dataviewId && x.id === dataviewId) ? (
                            <>
                                <InputTitle>{t('dataviewColumnsInput.label')}</InputTitle>
                                <TransferList
                                    items={dataviewFields}
                                    width={330}
                                    onChange={(/** @type {import('@commonClient2/../typings/transferList').TransferListItem[]} */
                                        transferListResult,
                                    ) => updateSetupStore('dataviewDisplayedCols', transferListResult.filter((x) => x.selected)
                                        .map((x) => x.id.toString()))}
                                    useSearchBars
                                    useSelectAll
                                    titleLeft={t('dataviewColumnsInput.hidden')}
                                    titleRight={t('dataviewColumnsInput.visible')}
                                    useTotalLeft
                                    useTotalRight
                                    disabled={disabledSections.dataviewDisplayedCols}
                                />
                            </>
                        ) : <></>
                    }
                    {
                        validationErrors.dataviewDisplayedCols ? (
                            <span style={{ color: 'red' }}>{t('dataviewColumnsInput.error')}</span>) : null
                    }

                </div>
            </CardContent>
        </Card>
    );
}

export default DataviewSection;
